import { useEffect, useRef, useState } from 'react'
import { searchCourses } from '../lib/golfCourseApi.js'

/**
 * Course + tee picker used inside the tournament create / edit modals
 * whenever handicap mode is on. Hits the live golfcourseapi.com search
 * endpoint, lets the director pick a course, then a tee box, and bubbles
 * the resolved slope / rating / par / tee-name up via onChange.
 *
 * Controlled by `value`. The parent persists each field individually on
 * tournament.info so the picker can be skipped entirely when handicap
 * mode = 'none'.
 *
 *   value = { courseName, courseId, slopeRating, courseRating,
 *             coursePar, teeName, teeBoxes }
 */
export default function CourseSearchPicker({ value, onChange }) {
  const [query, setQuery] = useState(value?.courseName || '')
  const [results, setResults] = useState([])
  const [loading, setLoading] = useState(false)
  const [open, setOpen] = useState(false)
  const [error, setError] = useState('')
  // The picker keeps a copy of the last selected course's tee list so
  // the user can re-pick a tee without re-searching. Hydrated from value
  // for the edit-flow case where the modal opens already populated.
  const [teeBoxes, setTeeBoxes] = useState(
    Array.isArray(value?.teeBoxes) ? value.teeBoxes : [],
  )
  const debRef = useRef(null)

  // Debounced search. 350ms is the same delay the GolfGames mobile
  // search uses — feels instant on a desktop connection without
  // hammering the API on every keystroke.
  useEffect(() => {
    if (debRef.current) clearTimeout(debRef.current)
    const q = (query || '').trim()
    if (q.length < 3 || q === value?.courseName) {
      setResults([])
      setLoading(false)
      return
    }
    debRef.current = setTimeout(async () => {
      setLoading(true)
      setError('')
      try {
        const hits = await searchCourses(q)
        setResults(hits)
        setOpen(true)
        if (hits.length === 0) setError('No matches — try fewer words.')
      } catch {
        setError('Search failed — check connection and retry.')
      } finally {
        setLoading(false)
      }
    }, 350)
    return () => debRef.current && clearTimeout(debRef.current)
  }, [query, value?.courseName])

  const pickCourse = (hit) => {
    setQuery(hit.name)
    setResults([])
    setOpen(false)
    setTeeBoxes(hit.teeBoxes || [])
    // Auto-pick the only tee box when there's just one. Otherwise wait
    // for the director to choose from the dropdown.
    if ((hit.teeBoxes || []).length === 1) {
      const t = hit.teeBoxes[0]
      onChange({
        courseName: hit.name,
        courseId: hit.id,
        coursePar: t.par || hit.par,
        slopeRating: t.slope,
        courseRating: t.rating,
        teeName: t.name,
        teeBoxes: hit.teeBoxes,
      })
    } else {
      onChange({
        courseName: hit.name,
        courseId: hit.id,
        coursePar: hit.par,
        slopeRating: null,
        courseRating: null,
        teeName: null,
        teeBoxes: hit.teeBoxes,
      })
    }
  }

  const pickTee = (teeName) => {
    const t = (teeBoxes || []).find((x) => x.name === teeName)
    if (!t) return
    onChange({
      courseName: value?.courseName || '',
      courseId: value?.courseId || null,
      coursePar: t.par || value?.coursePar || null,
      slopeRating: t.slope,
      courseRating: t.rating,
      teeName: t.name,
      teeBoxes,
    })
  }

  const hasCourse = !!value?.courseName
  const hasTee = !!value?.teeName && Number(value?.slopeRating) > 0

  return (
    <div className="space-y-3">
      <div className="relative">
        <label className="block">
          <span className="text-xs uppercase tracking-widest font-bold text-gray-600">Course</span>
          <input
            className="input mt-1"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onFocus={() => results.length > 0 && setOpen(true)}
            placeholder="Type at least 3 letters to search…"
            autoComplete="off" />
        </label>
        {loading ? (
          <p className="text-xs text-gray-500 mt-1">Searching…</p>
        ) : null}
        {error && !loading ? (
          <p className="text-xs text-red-600 mt-1">{error}</p>
        ) : null}
        {open && results.length > 0 ? (
          <div className="absolute z-10 left-0 right-0 mt-1 max-h-60 overflow-y-auto bg-white border border-gray-200 rounded-lg shadow-lg">
            {results.map((r) => (
              <button
                key={r.id}
                type="button"
                onClick={() => pickCourse(r)}
                className="block w-full text-left px-3 py-2 hover:bg-gray-50 border-b last:border-0">
                <div className="font-semibold text-sm">{r.name}</div>
                <div className="text-xs text-gray-500">
                  {r.location || '—'} · Par {r.par} · {r.teeBoxes.length} tee{r.teeBoxes.length === 1 ? '' : 's'}
                </div>
              </button>
            ))}
          </div>
        ) : null}
      </div>
      {hasCourse && teeBoxes.length > 0 ? (
        <label className="block">
          <span className="text-xs uppercase tracking-widest font-bold text-gray-600">Tee Box</span>
          <select
            className="input mt-1"
            value={value?.teeName || ''}
            onChange={(e) => pickTee(e.target.value)}>
            <option value="">Select a tee box…</option>
            {teeBoxes.map((t) => (
              <option key={`${t.name}-${t.rating}-${t.slope}`} value={t.name}>
                {t.name} — Rating {t.rating} / Slope {t.slope} / Par {t.par}
              </option>
            ))}
          </select>
        </label>
      ) : null}
      {hasTee ? (
        <div className="rounded-lg px-3 py-2 text-sm bg-gray-50 border border-gray-200 flex justify-between">
          <span className="uppercase tracking-widest text-gray-500 text-xs">Rating · Slope · Par</span>
          <span className="font-extrabold">{value.courseRating} · {value.slopeRating} · {value.coursePar}</span>
        </div>
      ) : null}
    </div>
  )
}
